/**
 * Music glyphs drawn as SVG path data in staff spaces (1 = the distance between two staff lines),
 * after the proportions of SMuFL fonts (Bravura) and Gould's "Behind Bars". No music font is
 * needed: every symbol is a path.
 */

const f = (n: number) => (Math.round(n * 1000) / 1000).toString();

/** An ellipse of semi-axes a, b rotated by deg, centred at (cx, cy), as a closed path. */
export function ellipse(cx: number, cy: number, a: number, b: number, deg: number, sweep = 1): string {
  const r = (deg * Math.PI) / 180;
  const dx = a * Math.cos(r);
  const dy = a * Math.sin(r);
  return `M${f(cx - dx)},${f(cy - dy)}A${f(a)},${f(b)} ${f(deg)} 1,${sweep} ${f(cx + dx)},${f(cy + dy)}A${f(a)},${f(b)} ${f(deg)} 1,${sweep} ${f(cx - dx)},${f(cy - dy)}Z`;
}

export const HEAD_W = 1.18;

/** A black notehead whose left edge is at x. */
export function blackHead(x: number, y: number): string {
  return ellipse(x + HEAD_W / 2, y, 0.62, 0.43, -20);
}

/** A half notehead (outline and hole; draw with the even-odd rule). */
export function halfHead(x: number, y: number): string {
  return ellipse(x + HEAD_W / 2, y, 0.62, 0.43, -20) + ellipse(x + HEAD_W / 2, y, 0.5, 0.2, -32, 0);
}

export const WHOLE_W = 1.62;

/** A whole note (outline and slanted hole; even-odd). */
export function wholeHead(x: number, y: number): string {
  return ellipse(x + WHOLE_W / 2, y, 0.81, 0.5, 0) + ellipse(x + WHOLE_W / 2 + 0.02, y, 0.36, 0.26, 58, 0);
}

export function rect(x0: number, y0: number, x1: number, y1: number): string {
  return `M${f(x0)},${f(y0)}H${f(x1)}V${f(y1)}H${f(x0)}Z`;
}

export function poly(points: Array<[number, number]>): string {
  return `M${points.map(([x, y]) => `${f(x)},${f(y)}`).join('L')}Z`;
}

export function circle(cx: number, cy: number, r: number): string {
  return ellipse(cx, cy, r, r, 0);
}

/** A beam: a parallelogram from (x0, y0) to (x1, y1) along its outer edge, thickness t toward the noteheads (dir 1 = down). */
export function beam(x0: number, y0: number, x1: number, y1: number, inward: number, t = 0.5): string {
  return poly([
    [x0, y0],
    [x1, y1],
    [x1, y1 + inward * t],
    [x0, y0 + inward * t],
  ]);
}

/** One flag on a stem tip at (x, y): up = the stem points up (flag hangs down to the right). */
export function flag(x: number, y: number, up: boolean): string {
  const s = up ? 1 : -1;
  const p = (dx: number, dy: number) => `${f(x + dx)},${f(y + s * dy)}`;
  return `M${p(0, 0)}C${p(0.08, 0.95)} ${p(1.28, 1.25)} ${p(0.98, 2.95)}C${p(1.05, 1.95)} ${p(0.55, 1.42)} ${p(0, 1.12)}Z`;
}

/* ---------- Accidentals (centred on the note's line or space; x is the glyph's centre) ---------- */

export const ACC_W: Record<number, number> = { 1: 0.98, [-1]: 0.9, 0: 0.7, 2: 0.98, [-2]: 1.7 };

export function sharp(x: number, y: number): string {
  const bar = (cy: number) =>
    poly([
      [x - 0.47, cy + 0.17 - 0.19],
      [x + 0.47, cy - 0.17 - 0.19],
      [x + 0.47, cy - 0.17 + 0.19],
      [x - 0.47, cy + 0.17 + 0.19],
    ]);
  return rect(x - 0.27, y - 1.12, x - 0.15, y + 1.4) + rect(x + 0.15, y - 1.4, x + 0.27, y + 1.12) + bar(y - 0.5) + bar(y + 0.5);
}

/** Flat: stem and bowl (bowl hole: even-odd). */
export function flat(x: number, y: number): string {
  const p = (dx: number, dy: number) => `${f(x + dx)},${f(y + dy)}`;
  const stem = rect(x - 0.42, y - 1.75, x - 0.3, y + 0.52);
  const outer = `M${p(-0.34, 0.52)}C${p(0.3, 0.18)} ${p(0.66, -0.25)} ${p(0.42, -0.55)}C${p(0.2, -0.82)} ${p(-0.12, -0.6)} ${p(-0.34, -0.32)}Z`;
  const inner = `M${p(-0.3, 0.26)}C${p(0.1, 0.02)} ${p(0.36, -0.28)} ${p(0.22, -0.43)}C${p(0.08, -0.58)} ${p(-0.14, -0.4)} ${p(-0.3, -0.16)}Z`;
  return stem + outer + inner;
}

export function natural(x: number, y: number): string {
  const bar = (cy: number) =>
    poly([
      [x - 0.3, cy + 0.11 - 0.18],
      [x + 0.3, cy - 0.11 - 0.18],
      [x + 0.3, cy - 0.11 + 0.18],
      [x - 0.3, cy + 0.11 + 0.18],
    ]);
  return rect(x - 0.31, y - 1.35, x - 0.2, y + 0.52) + rect(x + 0.2, y - 0.52, x + 0.31, y + 1.35) + bar(y - 0.32) + bar(y + 0.32);
}

export function doubleSharp(x: number, y: number): string {
  const d = 0.42;
  const t = 0.13;
  return poly([
    [x - d, y - d + t],
    [x - d + t, y - d],
    [x + d, y + d - t],
    [x + d - t, y + d],
  ]) + poly([
    [x + d - t, y - d],
    [x + d, y - d + t],
    [x - d + t, y + d],
    [x - d, y + d - t],
  ]);
}

export function accidental(alter: number, x: number, y: number): { fill: string; eo: string } {
  if (alter === 1) return { fill: sharp(x, y), eo: '' };
  if (alter === 0) return { fill: natural(x, y), eo: '' };
  if (alter === 2) return { fill: doubleSharp(x, y), eo: '' };
  if (alter === -1) return { fill: '', eo: flat(x, y) };
  return { fill: '', eo: flat(x - 0.42, y) + flat(x + 0.42, y) };
}

/* ---------- Rests: x is the centre, y the reference line (see each) ---------- */

/** Whole rest hanging from the line at y. */
export function wholeRest(x: number, y: number): string {
  return rect(x - 0.62, y, x + 0.62, y + 0.5);
}

/** Half rest sitting on the line at y. */
export function halfRest(x: number, y: number): string {
  return rect(x - 0.62, y - 0.5, x + 0.62, y);
}

/** A straight stroke from p to q of thickness t, wound clockwise (so overlapping strokes add up). */
function stroke(p: [number, number], q: [number, number], t: number): string {
  const dx = q[0] - p[0];
  const dy = q[1] - p[1];
  const L = Math.hypot(dx, dy) || 1;
  const nx = (-dy / L) * (t / 2);
  const ny = (dx / L) * (t / 2);
  const pts: Array<[number, number]> = [
    [p[0] - nx, p[1] - ny],
    [q[0] - nx, q[1] - ny],
    [q[0] + nx, q[1] + ny],
    [p[0] + nx, p[1] + ny],
  ];
  // Clockwise on screen: the signed area must be positive (y points down).
  let area = 0;
  for (let i = 0; i < 4; i++) area += pts[i][0] * pts[(i + 1) % 4][1] - pts[(i + 1) % 4][0] * pts[i][1];
  return poly(area > 0 ? pts : pts.reverse());
}

/** Quarter rest centred on y: a zigzag of thick and thin strokes ending in a hook. */
export function quarterRest(x: number, y: number): string {
  const P = (dx: number, dy: number): [number, number] => [x + dx, y + dy];
  const p0 = P(-0.22, -1.5);
  const p1 = P(0.4, -0.68);
  const p2 = P(-0.3, 0.02);
  const p3 = P(0.36, 0.78);
  const p = (dx: number, dy: number) => `${f(x + dx)},${f(y + dy)}`;
  // Drawn clockwise, like the strokes, so that where they overlap the ink adds up.
  const hook = `M${p(0.3, 0.86)}C${p(-0.02, 0.52)} ${p(-0.4, 0.5)} ${p(-0.5, 0.82)}C${p(-0.62, 1.12)} ${p(-0.26, 1.42)} ${p(0.14, 1.5)}C${p(0.04, 1.42)} ${p(-0.1, 1.3)} ${p(-0.22, 1.08)}C${p(-0.48, 0.62)} ${p(0.05, 0.42)} ${p(0.42, 0.7)}Z`;
  return stroke(p0, p1, 0.17) + stroke(P(0.4, -0.7), p2, 0.42) + stroke(P(-0.3, 0.0), p3, 0.17) + hook;
}

/** Eighth, 16th, 32nd … rest (n hooks), centred on y. */
export function hookRest(x: number, y: number, n: number): string {
  const p = (dx: number, dy: number) => `${f(x + dx)},${f(y + dy)}`;
  const top = -0.5 - (n - 1) * 0.5;
  const bottom = 1.0 + (n - 1) * 0.5;
  const slope = 0.28; // x per unit y going down-left
  const stemX = (dy: number) => 0.42 - (dy - top) * slope;
  let d = `M${p(stemX(top) - 0.02, top - 0.18)}L${p(stemX(top) + 0.1, top - 0.12)}L${p(stemX(bottom) + 0.06, bottom)}L${p(stemX(bottom) - 0.08, bottom)}Z`;
  for (let i = 0; i < n; i++) {
    const hy = top + i * 1.0;
    const sx = stemX(hy);
    d += circle(x + sx - 0.72, y + hy + 0.05, 0.27);
    d += `M${p(sx - 0.86, hy + 0.12)}C${p(sx - 0.5, hy + 0.42)} ${p(sx - 0.2, hy + 0.2)} ${p(sx, hy - 0.12)}L${p(sx + 0.08, hy - 0.08)}C${p(sx - 0.12, hy + 0.36)} ${p(sx - 0.5, hy + 0.6)} ${p(sx - 0.9, hy + 0.32)}Z`;
  }
  return d;
}

/* ---------- Clefs (stroked; origin at the top line of the staff, x at the clef's left) ---------- */

export const TREBLE_CLEF =
  'M1.32 3.08C0.92 3.1 0.78 2.52 1.28 2.38C1.92 2.24 2.24 3.02 1.92 3.62C1.6 4.24 0.46 4.16 0.26 3.3C0.06 2.38 0.92 1.58 1.5 0.92C2.02 0.3 2.12 -0.92 1.7 -1.32C1.3 -1.62 0.88 -0.62 0.98 0.32L1.52 5.02C1.6 5.62 1.02 6.02 0.6 5.62';
export const TREBLE_BALL: [number, number, number] = [0.78, 5.5, 0.3];
export const BASS_CLEF = 'M0.22 1.02C0.18 0.2 1.02 -0.18 1.56 0.2C2.18 0.62 2.06 1.66 1.52 2.34C1.02 2.98 0.52 3.38 0.06 3.7';
export const BASS_DOTS: Array<[number, number, number]> = [
  [0.36, 1.02, 0.34],
  [2.4, 0.52, 0.17],
  [2.4, 1.52, 0.17],
];
/** Alto/tenor (C) clef: two blocks and two curls, centred on the line at cy (relative to the top line). */
export function cClef(x: number, cy: number): string {
  const p = (dx: number, dy: number) => `${f(x + dx)},${f(cy + dy)}`;
  return (
    rect(x, cy - 2, x + 0.5, cy + 2) +
    rect(x + 0.72, cy - 2, x + 0.88, cy + 2) +
    `M${p(0.88, 0)}L${p(1.25, -0.5)}C${p(1.6, -0.9)} ${p(2.3, -1.0)} ${p(2.3, -1.6)}C${p(2.3, -2.1)} ${p(1.6, -2.15)} ${p(1.4, -1.75)}L${p(1.3, -1.8)}C${p(1.5, -2.35)} ${p(2.6, -2.3)} ${p(2.6, -1.55)}C${p(2.6, -0.85)} ${p(1.7, -0.6)} ${p(1.2, -0.25)}Z` +
    `M${p(0.88, 0)}L${p(1.25, 0.5)}C${p(1.6, 0.9)} ${p(2.3, 1.0)} ${p(2.3, 1.6)}C${p(2.3, 2.1)} ${p(1.6, 2.15)} ${p(1.4, 1.75)}L${p(1.3, 1.8)}C${p(1.5, 2.35)} ${p(2.6, 2.3)} ${p(2.6, 1.55)}C${p(2.6, 0.85)} ${p(1.7, 0.6)} ${p(1.2, 0.25)}Z`
  );
}

/* ---------- Fermata, ties ---------- */

/** Fermata whose base line is at y (above: arc opens downward). */
export function fermata(x: number, y: number, above: boolean): string {
  const s = above ? 1 : -1;
  const p = (dx: number, dy: number) => `${f(x + dx)},${f(y - s * dy)}`;
  return `M${p(-1.2, 0)}C${p(-1.2, 1.55)} ${p(1.2, 1.55)} ${p(1.2, 0)}L${p(1.06, 0)}C${p(1.0, 1.2)} ${p(-1.0, 1.2)} ${p(-1.06, 0)}Z` + circle(x, y - s * 0.28, 0.2);
}

/** A tie or slur as a crescent from (x0, y0) to (x1, y1), bulging by h (negative = upward). */
export function tie(x0: number, y0: number, x1: number, y1: number, h: number): string {
  const dx = x1 - x0;
  const a = (k: number) => `${f(x0 + dx * 0.22)},${f(y0 + h * k)} ${f(x1 - dx * 0.22)},${f(y1 + h * k)}`;
  return `M${f(x0)},${f(y0)}C${a(1)} ${f(x1)},${f(y1)}C${a(0.7).split(' ').reverse().join(' ')} ${f(x0)},${f(y0)}Z`;
}

/** The brace joining the staves of a keyboard part, from y0 to y1, its right edge at x. */
export function brace(x: number, y0: number, y1: number): string {
  const h = y1 - y0;
  const m = y0 + h / 2;
  const p = (dx: number, yy: number) => `${f(x + dx)},${f(yy)}`;
  return (
    `M${p(0, y0)}C${p(-1.1, y0 + h * 0.08)} ${p(-0.2, m - h * 0.2)} ${p(-1.1, m)}` +
    `C${p(-0.2, m + h * 0.2)} ${p(-1.1, y1 - h * 0.08)} ${p(0, y1)}` +
    `C${p(-0.75, y1 - h * 0.1)} ${p(0.25, m + h * 0.2)} ${p(-0.82, m)}` +
    `C${p(0.25, m - h * 0.2)} ${p(-0.75, y0 + h * 0.1)} ${p(0, y0)}Z`
  );
}
